import { useQuery } from '@tanstack/react-query';
import { fetchData } from '../../../utils';
import useSmartlook from '../../useSmartlook';
import { LoanExtensionFee } from './useActiveLoanQuery';

export function useFirstLoanOfferQuery(
  {
    amount,
    term,
  }: {
    amount?: number;
    term?: number;
  },
  enabled = true,
) {
  const { error } = useSmartlook();
  return useQuery<Offer>({
    queryKey: ['application', 'first-loan-offer', { amount, term }],
    queryFn: async () => {
      try {
        const data = await fetchData(
          `/application/first-loan-offer?amount=${amount}&term=${term}`,
          {
            headers: {
              Accept: 'application/json',
            },
          },
        );
        return data as Offer;
      } catch (e) {
        error('[useConstraintsQuery][GET /application/first-loan-offer]', e);
        throw e;
      }
    },
    staleTime: Infinity,
    enabled: !!amount && !!term && enabled,
    placeholderData: (previousData) => previousData,
  });
}

export type Offer = {
  assignedInterest: number;
  term: number;
  newPrincipal: number;
  totalPrincipal: number;
  interestRate: number;
  newInterest: number;
  newInterestBeforeDiscount: number;
  firstPaymentInterest: number;
  currentPrincipal: number;
  currentPrincipalWithInterest: number;
  currentPrincipalWithInterestAndInitialCommission: number;
  monthlyPayment: number;
  agreementEndDate: string;
  isAdditionalAmount: boolean;
  discountAmount: number;
  discountPercent: number;
  discounts: {
    type: string;
    percent: number;
    amount: number;
  }[];
  annualPercentageRate: number;
  totalInterest: number;
  initialCommission: number;
  newInitialCommission: number;
  totalCostOfCredit: number;
  totalRepayableAmount: number;
  firstPaymentDate: string;
  extensionFees: {
    7: number;
    14: number;
    30: number;
  };
  loanExtensionFees: LoanExtensionFee[];
  futureExtensionFees: LoanExtensionFee[];
  newAmountUsedDays: number;
  newAmountWithInterestAndInitialCommission: number;
  newPrincipalApr: number;
  totalRoundedAmount: number;
  capitalizableCommission: number;
  newPrincipalWithAllCapitalizableFees: number;
  newCapitalizedInterest: number;
  _links: {
    self: {
      href: string;
      title: string;
    };
  };
};
